import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Settings, Users, Building2, Shield, FileText, Save, Plus,
  Trash2, X, Search, AlertCircle, UserPlus, Mail, Phone, Eye, Clock,
  CheckCircle, XCircle, FileCheck, MapPin, Calendar, RefreshCw, Globe,
  ChevronDown, ChevronUp, ChevronRight, Maximize2, Download, ExternalLink, MoreVertical
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import api from '../../services/apiService';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// Dossier Overlay Component
const DossierOverlay = ({ technician, onClose, authLevels }) => {
  const [activeDoc, setActiveDoc] = useState(null);
  const [docSearch, setDocSearch] = useState('');
  const documents = technician?.qualifications || [];

  const getDocStatus = (doc) => {
    if (doc.is_expired) return { status: 'expired', color: 'red', label: 'EXPIRED' };
    if (doc.expiry_date) {
      const daysUntil = Math.ceil((new Date(doc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 30) return { status: 'expiring', color: 'amber', label: 'EXPIRING SOON' };
    }
    return { status: 'valid', color: 'emerald', label: 'VALID' };
  };

  // Set first valid document as active by default (sorted: valid first)
  useEffect(() => {
    if (documents.length > 0 && !activeDoc) {
      const sorted = [...documents].sort((a, b) => {
        const aStatus = getDocStatus(a);
        const bStatus = getDocStatus(b);
        const order = { valid: 0, expiring: 1, expired: 2 };
        return order[aStatus.status] - order[bStatus.status];
      });
      setActiveDoc(sorted[0]);
    }
  }, [documents, activeDoc]);

  if (!technician) return null;

  // Filter documents by search
  const filteredDocuments = documents.filter(doc => {
    if (!docSearch) return true;
    const search = docSearch.toLowerCase();
    const docName = (doc.qualification_type_name || doc.name || doc.document_name || '').toLowerCase();
    return docName.includes(search);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-slate-900/80 backdrop-blur-sm">
      <div className="bg-white w-full max-w-7xl h-full max-h-[92vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-orange-500 shadow-lg">
              <FileCheck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Technical Dossier: {technician.full_name}</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Personnel Certification Database</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-xl transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content - Two Panels */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Document List */}
          <div className="w-[360px] border-r border-slate-100 bg-slate-50/50 p-5 space-y-5 overflow-y-auto">
            {/* User Info Card */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
              {technician.profile_picture ? (
                <img src={getImageUrl(technician.profile_picture)} alt={technician.full_name} className="w-16 h-16 rounded-2xl shadow-lg border-2 border-slate-100 object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-black text-xl shadow-lg">
                  {technician.first_name?.charAt(0)?.toUpperCase() || 'T'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-base font-black text-slate-900 truncate">{technician.full_name}</div>
                <div className="text-[10px] font-bold text-orange-500 uppercase mt-1">{technician.company_name}</div>
                {technician.email && (
                  <div className="text-[10px] text-slate-400 mt-1 truncate">{technician.email}</div>
                )}
              </div>
            </div>

            {/* Authorization Badge */}
            {technician.authorization && (
              <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-md">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-emerald-900 uppercase">Authorized - Level {technician.authorization.level_code}</p>
                    <p className="text-[10px] text-emerald-600 font-medium">
                      Valid until: {technician.authorization.valid_until ? new Date(technician.authorization.valid_until).toLocaleDateString('en-GB') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Document Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Search documents..."
                className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs font-medium"
              />
            </div>

            {/* Documents List */}
            <div>
              <div className="flex justify-between items-center mb-3 px-1">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Credentials Ledger</h4>
                <span className="text-[9px] font-black bg-slate-200 px-2 py-1 rounded-lg uppercase">{filteredDocuments.length} / {documents.length}</span>
              </div>

              {filteredDocuments.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-xs font-bold text-slate-500">{docSearch ? 'No matching documents' : 'No documents uploaded'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {[...filteredDocuments].sort((a, b) => {
                    const aStatus = getDocStatus(a);
                    const bStatus = getDocStatus(b);
                    const order = { valid: 0, expiring: 1, expired: 2 };
                    return order[aStatus.status] - order[bStatus.status];
                  }).map((doc) => {
                    const docStatus = getDocStatus(doc);
                    const isActive = activeDoc?.id === doc.id;

                    const borderColor = isActive
                      ? 'border-orange-600'
                      : docStatus.status === 'valid'
                        ? 'border-l-4 border-l-emerald-500 border-slate-200'
                        : docStatus.status === 'expiring'
                          ? 'border-l-4 border-l-amber-500 border-slate-200'
                          : 'border-l-4 border-l-red-500 border-slate-200';

                    return (
                      <button
                        key={doc.id}
                        onClick={() => setActiveDoc(doc)}
                        className={`w-full text-left p-4 rounded-2xl border transition-all flex items-center gap-4 ${
                          isActive
                            ? 'bg-orange-500 border-orange-600 text-white shadow-lg shadow-orange-500/20'
                            : `bg-white ${borderColor} hover:shadow-md`
                        }`}
                      >
                        <FileText className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : docStatus.status === 'valid' ? 'text-emerald-500' : docStatus.status === 'expiring' ? 'text-amber-500' : 'text-red-500'}`} />
                        <div className="overflow-hidden flex-1">
                          <div className={`text-xs font-black uppercase tracking-tight truncate ${isActive ? 'text-white' : 'text-slate-800'}`}>
                            {doc.qualification_type_name || doc.name || doc.document_name || 'Document'}
                          </div>
                          <div className={`text-[9px] font-bold mt-1 ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
                            EXP: {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString('en-GB') : 'No expiry'}
                          </div>
                        </div>
                        {!isActive && (
                          <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${
                            docStatus.status === 'valid' ? 'bg-emerald-100 text-emerald-600' :
                            docStatus.status === 'expiring' ? 'bg-amber-100 text-amber-600' :
                            'bg-red-100 text-red-600'
                          }`}>{docStatus.label}</span>
                        )}
                        {isActive && <ChevronRight className="h-4 w-4 text-white flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Document Preview */}
          <div className="flex-1 p-6 bg-white overflow-y-auto">
            {activeDoc ? (
              <div className="h-full flex flex-col">
                {/* Document Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-black text-slate-900 tracking-tight">{activeDoc.qualification_type_name || activeDoc.name || activeDoc.document_name || 'Document'}</h4>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Certificate ID: #{activeDoc.id || 'N/A'}</p>
                  </div>
                  <div className="flex gap-2">
                    {(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url) && (
                      <a
                        href={getImageUrl(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-lg"
                      >
                        <Download className="h-4 w-4" /> Export PDF
                      </a>
                    )}
                  </div>
                </div>

                {/* Status Badge - Always visible */}
                {(() => {
                  const docStatus = getDocStatus(activeDoc);
                  return (
                    <div className={`mb-4 p-4 border-2 rounded-2xl flex items-center justify-between ${
                      docStatus.color === 'emerald' ? 'border-emerald-200 bg-emerald-50' :
                      docStatus.color === 'amber' ? 'border-amber-200 bg-amber-50' :
                      'border-red-200 bg-red-50'
                    }`}>
                      <div className="flex items-center gap-3">
                        <CheckCircle className={`h-6 w-6 ${
                          docStatus.color === 'emerald' ? 'text-emerald-500' :
                          docStatus.color === 'amber' ? 'text-amber-500' :
                          'text-red-500'
                        }`} />
                        <div>
                          <div className={`text-xs font-black uppercase ${
                            docStatus.color === 'emerald' ? 'text-emerald-700' :
                            docStatus.color === 'amber' ? 'text-amber-700' :
                            'text-red-700'
                          }`}>Certificate {docStatus.label}</div>
                          <div className={`text-[9px] font-bold ${
                            docStatus.color === 'emerald' ? 'text-emerald-600' :
                            docStatus.color === 'amber' ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {activeDoc.expiry_date ? `Expires: ${new Date(activeDoc.expiry_date).toLocaleDateString('en-GB')}` : 'No expiry date'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-[8px] font-black uppercase ${
                          docStatus.color === 'emerald' ? 'text-emerald-400' :
                          docStatus.color === 'amber' ? 'text-amber-400' :
                          'text-red-400'
                        }`}>Status</div>
                        <div className={`text-lg font-black ${
                          docStatus.color === 'emerald' ? 'text-emerald-600' :
                          docStatus.color === 'amber' ? 'text-amber-600' :
                          'text-red-600'
                        }`}>{docStatus.label}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Document Preview Area */}
                <div className="flex-1 bg-slate-50 border-4 border-slate-100 rounded-3xl p-4 relative overflow-hidden min-h-[400px]">
                  {(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url) ? (
                    <iframe
                      src={getImageUrl(activeDoc.document || activeDoc.certificate_file || activeDoc.document_url)}
                      className="w-full h-full min-h-[350px] rounded-xl bg-white shadow-lg"
                      title="Document Preview"
                    />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center">
                      <div className="bg-white shadow-xl rounded-xl p-8 max-w-md text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <FileText className="h-8 w-8 text-slate-400" />
                        </div>
                        <h5 className="text-sm font-black text-slate-900 mb-2">No Document File</h5>
                        <p className="text-xs text-slate-500 mb-4">This certificate entry has no attached document file.</p>
                        <div className="text-[10px] text-slate-400 font-medium space-y-1">
                          <p><span className="font-bold">Holder:</span> {technician.full_name}</p>
                          <p><span className="font-bold">Company:</span> {technician.company_name}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-4">
                <Eye className="h-16 w-16 opacity-20" />
                <p className="text-xs font-black uppercase tracking-widest">Select a document to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const OEMProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Team data
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [newTeamMember, setNewTeamMember] = useState({
    user: null,
    role: 'master',
    can_approve_nominations: false,
    can_verify_documents: true,
    can_revoke_authorization: false,
    can_create_toolbox: true,
  });
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    role: 'hse_manager',
    can_approve_nominations: true,
    can_verify_documents: true,
    can_revoke_authorization: false,
    can_create_toolbox: true,
  });

  // Companies data
  const [projectCompanies, setProjectCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Authorization levels data
  const [authLevels, setAuthLevels] = useState([]);

  // Nominations data
  const [nominations, setNominations] = useState([]);

  // Project Assignments data
  const [projectAssignments, setProjectAssignments] = useState([]);

  // Certificate alerts
  const [certificateAlerts, setCertificateAlerts] = useState({ alerts: [], expiring_count: 0, expired_count: 0 });

  // Unified technicians table state
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [allTechniciansLoading, setAllTechniciansLoading] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('');
  const [authFilter, setAuthFilter] = useState('all');
  const [authLevelFilter, setAuthLevelFilter] = useState('');
  const [docStatusFilter, setDocStatusFilter] = useState('all');
  const [unifiedTechSearch, setUnifiedTechSearch] = useState('');
  const [selectedTechForDossier, setSelectedTechForDossier] = useState(null);

  // Sorting state for resource table
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Fetch functions
  const fetchProject = async () => {
    try {
      const response = await api.get(`/oem/projects/${id}/`);
      setProject(response.data);
    } catch (err) {
      console.error('Error fetching project:', err);
      toast.error('Failed to load project');
      navigate('/oem/projects');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get(`/oem/project-team/?project=${id}`);
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/users/');
      const teamUserIds = teamMembers.map(tm => tm.user);
      setAvailableUsers(response.data.filter(u => !teamUserIds.includes(u.id)));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchProjectCompanies = async () => {
    try {
      const response = await api.get(`/oem/project-companies/?project=${id}`);
      setProjectCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchAvailableCompanies = async () => {
    try {
      const response = await api.get(`/oem/available-companies/?project=${id}`);
      setAvailableCompanies(response.data);
    } catch (err) {
      console.error('Error fetching available companies:', err);
    }
  };

  const fetchAuthLevels = async () => {
    try {
      const response = await api.get('/oem/authorization-levels/');
      setAuthLevels(response.data);
    } catch (err) {
      console.error('Error fetching auth levels:', err);
    }
  };

  const fetchNominations = async () => {
    try {
      const response = await api.get(`/oem/nominations/?project=${id}`);
      setNominations(response.data);
    } catch (err) {
      console.error('Error fetching nominations:', err);
    }
  };

  const fetchProjectAssignments = async () => {
    try {
      const response = await api.get(`/oem/projects/${id}/authorized-technicians/`);
      setProjectAssignments(response.data);
    } catch (err) {
      console.error('Error fetching authorized technicians:', err);
    }
  };

  const fetchCertificateAlerts = async () => {
    try {
      const response = await api.get(`/oem/projects/${id}/certificate-alerts/`);
      setCertificateAlerts(response.data);
    } catch (err) {
      console.error('Error fetching certificate alerts:', err);
    }
  };

  const fetchAllTechnicians = async () => {
    setAllTechniciansLoading(true);
    try {
      const response = await api.get(`/oem/projects/${id}/all-technicians/`);
      setAllTechnicians(response.data);
    } catch (err) {
      console.error('Error fetching all technicians:', err);
      toast.error('Failed to load technicians');
    } finally {
      setAllTechniciansLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProject();
      await Promise.all([
        fetchTeamMembers(),
        fetchProjectCompanies(),
        fetchAuthLevels(),
        fetchNominations(),
        fetchProjectAssignments(),
        fetchCertificateAlerts(),
        fetchAllTechnicians(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [id]);

  // Handlers
  const handleUpdateProject = async () => {
    setSaving(true);
    try {
      await api.patch(`/oem/projects/${id}/`, {
        name: project.name,
        description: project.description,
        status: project.status,
        location: project.location,
        country: project.country,
        start_date: project.start_date,
        end_date: project.end_date || null,
      });
      toast.success('Project updated successfully');
    } catch (err) {
      console.error('Error updating project:', err);
      toast.error('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newTeamMember.user) {
      toast.error('Please select a user');
      return;
    }
    try {
      await api.post(`/oem/projects/${id}/team/`, newTeamMember);
      toast.success('Team member added');
      setShowAddTeamModal(false);
      setNewTeamMember({
        user: null,
        role: 'master',
        can_approve_nominations: false,
        can_verify_documents: true,
        can_revoke_authorization: false,
        can_create_toolbox: true,
      });
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error adding team member:', err);
      toast.error(err.response?.data?.error || 'Failed to add team member');
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await api.delete(`/oem/project-team/${memberId}/`);
      toast.success('Team member removed');
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error removing team member:', err);
      toast.error('Failed to remove team member');
    }
  };

  const handleCreateUserAndAddToTeam = async () => {
    if (!newUser.username || !newUser.email || !newUser.first_name || !newUser.last_name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newUser.password !== newUser.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setCreatingUser(true);
    try {
      const userResponse = await api.post('/users/', {
        username: newUser.username,
        email: newUser.email,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        password: newUser.password,
        is_active: true,
      });

      const createdUser = userResponse.data;

      await api.post(`/oem/projects/${id}/team/`, {
        user: createdUser.id,
        role: newUser.role,
        can_approve_nominations: newUser.can_approve_nominations,
        can_verify_documents: newUser.can_verify_documents,
        can_revoke_authorization: newUser.can_revoke_authorization,
        can_create_toolbox: newUser.can_create_toolbox,
      });

      toast.success(`User ${newUser.first_name} ${newUser.last_name} created and added to team!`);
      setShowCreateUserModal(false);
      setNewUser({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: '',
        role: 'hse_manager',
        can_approve_nominations: true,
        can_verify_documents: true,
        can_revoke_authorization: false,
        can_create_toolbox: true,
      });
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error creating user:', err);
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const errors = Object.entries(errorData)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
          toast.error(errors || 'Validation error');
        } else {
          toast.error(String(errorData.detail || errorData) || 'Failed to create user');
        }
      } else {
        toast.error('Failed to create user: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setCreatingUser(false);
    }
  };

  const handleAddCompany = async () => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }
    try {
      await api.post(`/oem/projects/${id}/companies/`, {
        company: selectedCompany.id
      });
      toast.success('Company added to project');
      setShowAddCompanyModal(false);
      setSelectedCompany(null);
      await fetchProjectCompanies();
      await fetchAvailableCompanies();
      await fetchAllTechnicians();
    } catch (err) {
      console.error('Error adding company:', err);
      toast.error(err.response?.data?.error || 'Failed to add company');
    }
  };

  // Helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
      active: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      completed: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      archived: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
    };
    return styles[status] || styles.draft;
  };

  const getRoleStyle = (role) => {
    const styles = {
      site_manager: 'bg-blue-50 text-blue-700 border-blue-200',
      hse_manager: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      master: 'bg-violet-50 text-violet-700 border-violet-200',
    };
    return styles[role] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const filteredUsers = availableUsers.filter(u => {
    if (!teamSearch) return true;
    const search = teamSearch.toLowerCase();
    return (
      u.username?.toLowerCase().includes(search) ||
      u.first_name?.toLowerCase().includes(search) ||
      u.last_name?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search)
    );
  });

  const filteredCompanies = availableCompanies.filter(c => {
    if (!companySearch) return true;
    const search = companySearch.toLowerCase();
    return (
      c.name?.toLowerCase().includes(search) ||
      c.address?.toLowerCase().includes(search) ||
      c.tax_id?.toLowerCase().includes(search)
    );
  });

  // Filter and sort technicians
  const filteredTechnicians = useMemo(() => {
    let result = (allTechnicians.technicians || []).filter(tech => {
      if (companyFilter && tech.company_id !== parseInt(companyFilter)) return false;
      if (authFilter === 'authorized' && !tech.authorization) return false;
      if (authFilter === 'pending' && !tech.pending_nomination) return false;
      if (authFilter === 'none' && (tech.authorization || tech.pending_nomination)) return false;
      if (authLevelFilter && (!tech.authorization || tech.authorization.level_id !== parseInt(authLevelFilter))) return false;
      if (docStatusFilter === 'valid' && tech.qualifications?.some(q => q.is_expired)) return false;
      if (docStatusFilter === 'expired' && !tech.qualifications?.some(q => q.is_expired)) return false;
      if (docStatusFilter === 'expiring') {
        const hasExpiring = tech.qualifications?.some(q => {
          if (!q.expiry_date || q.is_expired) return false;
          const daysUntil = Math.ceil((new Date(q.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
          return daysUntil <= 30;
        });
        if (!hasExpiring) return false;
      }
      if (docStatusFilter === 'no_docs' && tech.qualifications_count > 0) return false;
      if (unifiedTechSearch) {
        const search = unifiedTechSearch.toLowerCase();
        if (!tech.full_name?.toLowerCase().includes(search) && !tech.email?.toLowerCase().includes(search)) return false;
      }
      return true;
    });

    // Apply sorting
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let aVal, bVal;

        switch (sortColumn) {
          case 'docs':
            aVal = a.qualifications_count || 0;
            bVal = b.qualifications_count || 0;
            break;
          case 'auth':
            // Sort by: authorized (with level code) > pending > none
            aVal = a.authorization ? `1_${a.authorization.level_code}` : (a.pending_nomination ? '2_pending' : '3_none');
            bVal = b.authorization ? `1_${b.authorization.level_code}` : (b.pending_nomination ? '2_pending' : '3_none');
            break;
          case 'company':
            aVal = a.company_name?.toLowerCase() || '';
            bVal = b.company_name?.toLowerCase() || '';
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [allTechnicians, companyFilter, authFilter, authLevelFilter, docStatusFilter, unifiedTechSearch, sortColumn, sortDirection]);

  // Handle sort click
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 text-sm">Loading project...</span>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-md text-center">
          <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <h2 className="text-sm font-bold text-slate-900 mb-2">Project not found</h2>
          <button
            onClick={() => navigate('/oem/projects')}
            className="text-orange-500 hover:text-orange-600 text-xs font-bold"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusStyle(project.status);

  // Stats for KPI
  const stats = {
    team: teamMembers.length,
    companies: projectCompanies.filter(c => c.status === 'active').length,
    technicians: allTechnicians.total_count || 0,
    authorized: projectAssignments.length,
    pending: nominations.filter(n => n.status === 'submitted').length,
  };

  const tabs = [
    { id: 'overview', label: 'OVERVIEW' },
    { id: 'team', label: 'TEAM', count: stats.team },
    { id: 'resource', label: 'RESOURCE', count: stats.technicians },
    { id: 'authorized', label: 'AUTHORIZED', count: stats.authorized },
  ];

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/oem/projects')}
                  className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 text-slate-600" />
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Globe className="h-4 w-4 text-orange-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-base font-black text-slate-900">{project.code}</h1>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold rounded-md uppercase ${statusStyle.bg} ${statusStyle.text}`}>
                        <span className={`w-1 h-1 rounded-full ${statusStyle.dot}`} />
                        {project.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{project.name}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  fetchProject();
                  fetchTeamMembers();
                  fetchProjectCompanies();
                  fetchNominations();
                  fetchProjectAssignments();
                  fetchAllTechnicians();
                }}
                className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4 text-slate-600" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1.5 mt-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${
                    activeTab === tab.id
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] ${
                      activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Top Row - Contract Details & KPI */}
                <div className="grid grid-cols-3 gap-4">
                  {/* Contract Details */}
                  <div className="col-span-2 bg-slate-50 rounded-xl p-4">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-4">CONTRACT DETAILS</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Project Name</label>
                        <input
                          type="text"
                          value={project.name}
                          onChange={(e) => setProject({ ...project, name: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Project Code</label>
                        <input
                          type="text"
                          value={project.code}
                          disabled
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-slate-100 text-slate-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Location</label>
                        <input
                          type="text"
                          value={project.location || ''}
                          onChange={(e) => setProject({ ...project, location: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Country</label>
                        <input
                          type="text"
                          value={project.country || ''}
                          onChange={(e) => setProject({ ...project, country: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Start Date</label>
                        <input
                          type="date"
                          value={project.start_date || ''}
                          onChange={(e) => setProject({ ...project, start_date: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">End Date</label>
                        <input
                          type="date"
                          value={project.end_date || ''}
                          onChange={(e) => setProject({ ...project, end_date: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Status</label>
                        <select
                          value={project.status}
                          onChange={(e) => setProject({ ...project, status: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        >
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="archived">Archived</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <button
                          onClick={handleUpdateProject}
                          disabled={saving}
                          className="w-full h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-colors"
                        >
                          <Save className="h-3.5 w-3.5" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* KPI Monitor */}
                  <div className="bg-slate-900 rounded-xl p-4 text-white">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-4">KPI MONITOR</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-blue-500/20 flex items-center justify-center">
                            <Users className="h-3.5 w-3.5 text-blue-400" />
                          </div>
                          <span className="text-xs text-slate-300">Team</span>
                        </div>
                        <span className="text-lg font-black">{stats.team}</span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-emerald-500/20 flex items-center justify-center">
                            <Building2 className="h-3.5 w-3.5 text-emerald-400" />
                          </div>
                          <span className="text-xs text-slate-300">Companies</span>
                        </div>
                        <span className="text-lg font-black">{stats.companies}</span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-violet-500/20 flex items-center justify-center">
                            <Shield className="h-3.5 w-3.5 text-violet-400" />
                          </div>
                          <span className="text-xs text-slate-300">Authorized</span>
                        </div>
                        <span className="text-lg font-black text-emerald-400">{stats.authorized}</span>
                      </div>
                      <div className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-amber-500/20 flex items-center justify-center">
                            <Clock className="h-3.5 w-3.5 text-amber-400" />
                          </div>
                          <span className="text-xs text-slate-300">Pending</span>
                        </div>
                        <span className="text-lg font-black text-amber-400">{stats.pending}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Geo-Position & Timeline */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-3">GEO-POSITION</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{project.location || 'Not specified'}</p>
                        <p className="text-xs text-slate-500">{project.country || 'No country set'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-3">TIMELINE</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{formatDate(project.start_date)}</p>
                        <p className="text-xs text-slate-500">to {formatDate(project.end_date)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Team Tab */}
            {activeTab === 'team' && (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">PROJECT TEAM</h3>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => { fetchAvailableUsers(); setShowAddTeamModal(true); }}
                      className="h-8 px-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Add Existing
                    </button>
                    <button
                      onClick={() => setShowCreateUserModal(true)}
                      className="h-8 px-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Create New
                    </button>
                  </div>
                </div>

                {/* Team Grid */}
                {teamMembers.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mb-1">No team members yet</h3>
                    <p className="text-xs text-slate-500 mb-4">Add HSE managers, site managers, or admins</p>
                    <button
                      onClick={() => setShowCreateUserModal(true)}
                      className="text-orange-500 hover:text-orange-600 font-bold text-xs"
                    >
                      Create new team member →
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className="bg-slate-50 rounded-xl p-3 hover:bg-slate-100 transition-colors group"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-xs">
                              {member.user_first_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 text-sm">{member.user_first_name} {member.user_last_name}</p>
                              <p className="text-[10px] text-slate-500">{member.user_email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveTeamMember(member.id)}
                            className="w-6 h-6 rounded hover:bg-red-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-2 py-0.5 text-[9px] font-bold rounded border ${getRoleStyle(member.role)}`}>
                            {member.role_display}
                          </span>
                          {member.can_approve_nominations && (
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-emerald-100 text-emerald-700 rounded">APPROVE</span>
                          )}
                          {member.can_verify_documents && (
                            <span className="px-1.5 py-0.5 text-[8px] font-bold bg-blue-100 text-blue-700 rounded">VERIFY</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Resource Tab */}
            {activeTab === 'resource' && (
              <div className="space-y-4">
                {/* Header with filters */}
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">ALL TECHNICIANS</h3>
                  <button
                    onClick={() => { fetchAvailableCompanies(); setShowAddCompanyModal(true); }}
                    className="h-8 px-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Company
                  </button>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-xl p-3">
                  <div className="relative flex-1 min-w-[160px] max-w-[240px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="text"
                      value={unifiedTechSearch}
                      onChange={(e) => setUnifiedTechSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full h-8 pl-9 pr-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                    />
                  </div>
                  <select
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs"
                  >
                    <option value="">All Companies</option>
                    {allTechnicians.companies?.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    value={authFilter}
                    onChange={(e) => { setAuthFilter(e.target.value); if (e.target.value !== 'authorized') setAuthLevelFilter(''); }}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs"
                  >
                    <option value="all">All Status</option>
                    <option value="authorized">Authorized</option>
                    <option value="pending">Pending</option>
                    <option value="none">No Auth</option>
                  </select>
                  <select
                    value={docStatusFilter}
                    onChange={(e) => setDocStatusFilter(e.target.value)}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs"
                  >
                    <option value="all">All Docs</option>
                    <option value="valid">Valid</option>
                    <option value="expiring">Expiring</option>
                    <option value="expired">Expired</option>
                  </select>
                  {(companyFilter || authFilter !== 'all' || docStatusFilter !== 'all' || unifiedTechSearch) && (
                    <button
                      onClick={() => {
                        setCompanyFilter('');
                        setAuthFilter('all');
                        setAuthLevelFilter('');
                        setDocStatusFilter('all');
                        setUnifiedTechSearch('');
                      }}
                      className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Clear
                    </button>
                  )}
                  <span className="text-[10px] font-bold text-slate-500 ml-auto">
                    {filteredTechnicians.length} of {allTechnicians.total_count || 0}
                  </span>
                </div>

                {/* Table */}
                <div className="bg-slate-50 rounded-xl overflow-hidden">
                  {allTechniciansLoading ? (
                    <div className="p-10 text-center">
                      <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="text-xs text-slate-500 mt-3">Loading...</p>
                    </div>
                  ) : !allTechnicians.technicians?.length ? (
                    <div className="text-center py-10">
                      <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-xs text-slate-500">No technicians found</p>
                    </div>
                  ) : (
                    <div className="overflow-auto max-h-[calc(100vh-340px)]">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-slate-100 z-10">
                          <tr>
                            <th className="text-left py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Technician</th>
                            <th
                              onClick={() => handleSort('company')}
                              className="text-left py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-orange-500 transition-colors select-none"
                            >
                              <div className="flex items-center gap-1">
                                Company
                                {sortColumn === 'company' && (
                                  sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                                )}
                              </div>
                            </th>
                            <th className="text-left py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Contact</th>
                            <th
                              onClick={() => handleSort('docs')}
                              className="text-center py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-orange-500 transition-colors select-none"
                            >
                              <div className="flex items-center justify-center gap-1">
                                Docs
                                {sortColumn === 'docs' && (
                                  sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                                )}
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort('auth')}
                              className="text-center py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-orange-500 transition-colors select-none"
                            >
                              <div className="flex items-center justify-center gap-1">
                                Auth
                                {sortColumn === 'auth' && (
                                  sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                                )}
                              </div>
                            </th>
                            <th className="text-center py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Dossier</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {filteredTechnicians.map((tech) => (
                            <tr
                              key={tech.id}
                              className="bg-white hover:bg-orange-50/50 transition-colors"
                            >
                              <td className="py-2.5 px-4">
                                <div className="flex items-center gap-2">
                                  {tech.profile_picture ? (
                                    <img src={getImageUrl(tech.profile_picture)} alt={tech.full_name} className="w-7 h-7 rounded-lg object-cover" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-[10px]">
                                      {tech.first_name?.charAt(0)?.toUpperCase() || 'T'}
                                    </div>
                                  )}
                                  <span className="font-medium text-slate-900 text-xs">{tech.full_name}</span>
                                </div>
                              </td>
                              <td className="py-2.5 px-4">
                                <span className="text-xs text-slate-600">{tech.company_name}</span>
                              </td>
                              <td className="py-2.5 px-4">
                                <p className="text-xs text-slate-600">{tech.email}</p>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                                  tech.qualifications?.some(q => q.is_expired)
                                    ? 'bg-red-100 text-red-700'
                                    : tech.qualifications_count > 0
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-slate-100 text-slate-500'
                                }`}>
                                  <FileText className="h-3 w-3" />
                                  {tech.qualifications_count || 0}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                {tech.authorization ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">
                                    <Shield className="h-3 w-3" />
                                    {tech.authorization.level_code}
                                  </span>
                                ) : tech.pending_nomination ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">
                                    <Clock className="h-3 w-3" />
                                    Pending
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500">
                                    None
                                  </span>
                                )}
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <button
                                  onClick={() => setSelectedTechForDossier(tech)}
                                  className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-[9px] font-bold uppercase tracking-wide hover:bg-orange-600 transition-colors"
                                >
                                  DOCS
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Authorized Tab */}
            {activeTab === 'authorized' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">AUTHORIZED TECHNICIANS</h3>
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-bold">
                    {projectAssignments.length} AUTHORIZED
                  </span>
                </div>

                {projectAssignments.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 rounded-xl">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Shield className="h-6 w-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mb-1">No authorized technicians</h3>
                    <p className="text-xs text-slate-500">Technicians will appear here once authorized</p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl overflow-hidden">
                    <div className="overflow-auto max-h-[calc(100vh-300px)]">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-slate-100 z-10">
                          <tr>
                            <th className="text-left py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Technician</th>
                            <th className="text-left py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Company</th>
                            <th className="text-center py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Level</th>
                            <th className="text-center py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Valid Until</th>
                            <th className="text-center py-2.5 px-4 text-[9px] font-bold text-slate-500 uppercase tracking-wide">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {projectAssignments.map((auth) => (
                            <tr key={auth.id} className="bg-white hover:bg-emerald-50/50 transition-colors">
                              <td className="py-2.5 px-4">
                                <div className="flex items-center gap-2">
                                  {auth.profile_picture ? (
                                    <img src={getImageUrl(auth.profile_picture)} alt={auth.user_name} className="w-7 h-7 rounded-lg object-cover" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-[10px]">
                                      {auth.user_first_name?.charAt(0)?.toUpperCase() || auth.user_name?.charAt(0)?.toUpperCase() || 'T'}
                                    </div>
                                  )}
                                  <span className="font-medium text-slate-900 text-xs">{auth.user_name || `${auth.user_first_name || ''} ${auth.user_last_name || ''}`.trim()}</span>
                                </div>
                              </td>
                              <td className="py-2.5 px-4">
                                <span className="text-xs text-slate-600">{auth.company_name}</span>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-700">
                                  <Shield className="h-3 w-3" />
                                  {auth.authorization_level_code}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                <span className="text-xs text-slate-600">
                                  {auth.valid_until ? formatDate(auth.valid_until) : 'No expiry'}
                                </span>
                              </td>
                              <td className="py-2.5 px-4 text-center">
                                {(() => {
                                  const isExpired = auth.valid_until && new Date(auth.valid_until) < new Date();
                                  const isActive = !isExpired;
                                  return (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                                      isActive
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}>
                                      {isActive ? (
                                        <>
                                          <CheckCircle className="h-3 w-3" />
                                          Active
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="h-3 w-3" />
                                          Expired
                                        </>
                                      )}
                                    </span>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dossier Overlay */}
      {selectedTechForDossier && (
        <DossierOverlay
          technician={selectedTechForDossier}
          onClose={() => setSelectedTechForDossier(null)}
          authLevels={authLevels}
        />
      )}

      {/* Add Team Member Modal */}
      {showAddTeamModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900">Add Team Member</h2>
              <button
                onClick={() => setShowAddTeamModal(false)}
                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                />
              </div>
              <div className="max-h-40 overflow-auto space-y-1.5">
                {filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => setNewTeamMember({ ...newTeamMember, user: u.id })}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      newTeamMember.user === u.id
                        ? 'bg-orange-50 border border-orange-300'
                        : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                    }`}
                  >
                    <p className="font-medium text-slate-900 text-xs">{u.first_name} {u.last_name}</p>
                    <p className="text-[10px] text-slate-500">{u.email}</p>
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Role</label>
                <select
                  value={newTeamMember.role}
                  onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                >
                  <option value="master">Master</option>
                  <option value="site_manager">Site Manager</option>
                  <option value="hse_manager">HSE Manager</option>
                </select>
              </div>
              <button
                onClick={handleAddTeamMember}
                className="w-full h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-bold uppercase tracking-wide text-[10px] transition-colors"
              >
                Add to Team
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900">Create New User</h2>
              <button
                onClick={() => setShowCreateUserModal(false)}
                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="p-5 space-y-3 overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">First Name</label>
                  <input
                    type="text"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Last Name</label>
                  <input
                    type="text"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Password</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Confirm</label>
                  <input
                    type="password"
                    value={newUser.confirm_password}
                    onChange={(e) => setNewUser({ ...newUser, confirm_password: e.target.value })}
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                >
                  <option value="master">Master</option>
                  <option value="site_manager">Site Manager</option>
                  <option value="hse_manager">HSE Manager</option>
                </select>
              </div>
              <button
                onClick={handleCreateUserAndAddToTeam}
                disabled={creatingUser}
                className="w-full h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-bold uppercase tracking-wide text-[10px] transition-colors"
              >
                {creatingUser ? 'Creating...' : 'Create & Add to Team'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900">Add Company</h2>
              <button
                onClick={() => setShowAddCompanyModal(false)}
                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs"
                />
              </div>
              <div className="max-h-52 overflow-auto space-y-1.5">
                {filteredCompanies.length === 0 ? (
                  <div className="text-center py-6">
                    <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">No available companies</p>
                  </div>
                ) : (
                  filteredCompanies.map(c => (
                    <div
                      key={c.id}
                      onClick={() => setSelectedCompany(c)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedCompany?.id === c.id
                          ? 'bg-orange-50 border border-orange-300'
                          : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                      }`}
                    >
                      <p className="font-medium text-slate-900 text-xs">{c.name}</p>
                      <p className="text-[10px] text-slate-500">{c.address}</p>
                    </div>
                  ))
                )}
              </div>
              <button
                onClick={handleAddCompany}
                disabled={!selectedCompany}
                className="w-full h-9 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-bold uppercase tracking-wide text-[10px] transition-colors"
              >
                Add Company
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OEMProjectDetail;
